import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import Markdown from '@configuratorware/configurator-frontendgui/Framework/Components/Markdown';
import SvgIcon from '@material-ui/core/SvgIcon';
import ArrowIcon from './ArrowIcon';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';

const useStyles = makeStyles(
  (theme) => ({
    card: {
      borderRadius: 0,
      border: '1px solid #D9D9D9',
      boxShadow: 'none',
      height: '100%',
    },
    header: {
      position: 'relative',
      backgroundColor: '#A0C513',
      height: 170,
      [theme.breakpoints.up('sm')]: {
        height: 320,
      },
    },
    image: {
      height: '100%',
      width: '100%',
      objectFit: 'cover',
      '&+$milieu': {
        position: 'absolute',
        top: 0,
        left: 0,
      },
    },
    milieu: {
      height: 50,
      fontFamily: 'Inter',
      backgroundColor: 'white !important',
      color: '#A0C513 !important',
      borderRadius: 0,
      boxShadow: 'none',
      margin: 30,
      fontWeight: 'bold',
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    title: {
      color: '#A0C513',
      fontSize: 20,
      marginBottom: 12,
    },
    button: {
      background: '#A0C513',
      color: 'white',
      borderRadius: 0,
      boxShadow: 'none',
      fontFamily: 'Inter',
      marginBottom: 10,
      height: 50,
      '&:hover': {
        backgroundColor: 'black',
      },
      [theme.breakpoints.up('sm')]: {
        minWidth: 280,
      },
    },
    buttons: {
      marginTop: 30,
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
    },
    content: {
      margin: 30,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    boldText: {
      fontFamily: 'Inter',
      fontWeight: 500,
    },
    priceInfo: {
      backgroundColor: '#F5F5F5',
      top: 0,
      right: 0,
      padding: 15,
      height: 'fit-content',
    },
    price: {
      color: '#A0C513',
    },
    abstract: {
      '& ul': {
        fontFamily: 'Inter',
        listStyle: 'none',
        paddingLeft: 0,
        lineHeight: 2,
      },
      '& li': {
        display: 'flex',
      },
      '& li::before': {
        content: '"\\25A0"',
        color: '#A0C513',
        width: '1em',
        marginRight: 10,
      },
    },
    contentInfo: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
    },
    moreInfo: {
      height: 50,
    },
  }),
  {
    name: 'ItemTile',
  }
);
const ItemTile = (props) => {
  const { milieu, title, abstract, linkTo, image, price, pdfLink } = props;
  const classes = useStyles();

  const hidePrices = getConf('hidePrices');

  const renderPriceInfo = () => {
    return price ? (
      <Box className={classes.priceInfo}>
        <Typography component="div" className={classes.boldText}>
          {T('itemOverview.individualBed')}
        </Typography>
        <Typography variant="h6" component="div" className={classes.price}>
          {T('itemOverview.price', { price })}
        </Typography>
      </Box>
    ) : null;
  };

  return (
    <Card className={classes.card}>
      <div className={classes.header}>
        {image && <img src={image} className={classes.image} />}
        {milieu && (
          <Button className={classes.milieu} variant="contained" disabled>
            {milieu}
          </Button>
        )}
      </div>
      <Box className={classes.content}>
        <Grid container className={classes.contentInfo}>
          <Grid item xs={12} sm={12} md={7}>
            <Typography className={classes.title}>{title}</Typography>
            <Markdown MdComponent={Typography} className={classes.abstract}>
              {abstract}
            </Markdown>
          </Grid>
          {hidePrices ? null : (
            <Grid item xs={12} sm={12} md={5}>
              {renderPriceInfo()}
            </Grid>
          )}
        </Grid>
        <Box className={classes.buttons}>
          <Button
            className={classes.button}
            variant="contained"
            startIcon={<SvgIcon component={ArrowIcon} inheritViewBox />}
            href={linkTo}
          >
            {T('itemOverview.configureNow')}
          </Button>
          <Button startIcon={<AddIcon />} href={pdfLink} target="_blank" className={classes.moreInfo}>
            {T('itemOverview.moreInfo')}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

ItemTile.propTypes = {
  milieu: PropTypes.string,
  title: PropTypes.string,
  abstract: PropTypes.string,
  linkTo: PropTypes.string,
  image: PropTypes.string,
  price: PropTypes.string,
  pdfLink: PropTypes.string,
};

export default ItemTile;
